import type { Course, CourseContent } from "@/types/courseTypes"
import type { FreeMaterialReview } from "@/types/freeMaterialsTypes"
import type { Lecture } from "@/types/lectureTypes"
import type { ProfileLinks } from "@/types/profileTypes"
import type { StudentProject } from "@/types/projectTypes"
import type { Round } from "@/types/roundTypes"
import type { SkillDTO } from "@/types/skillsTypes"
import type { TaskSubmission } from "@/types/taskSubmissionTypes"
import type { NestedValues } from "@/types/utils"

export const MODALS = {
  advertisements: {
    index: "advertisementModal",
  },
  confirm: {
    index: "confirmModal",
  },
  notifications: {
    view: "notificationView",
  },
  enrolledRounds: {
    notes: "roundNotes",
  },
  lecture: {
    video: "videoModal",
    uplaodTask: "taskupload",
    preivewDrive: "driveVideoPreview",
    previewAdditionalMaterial: "additionalMaterialPreview",
    tasks: "lectureTasks",
    projects: "lectureProjects",
    mentorSessions: "lectureMentorSessions",
    additionalMaterials: "lectureAdditionalMaterial",
    content: "lectureContent",
    attend: "attendLecture",
  },
  feedbacks: {
    index: "feedback",
    all: "allFeedbacks",
  },
  profile: {
    editSocialLinks: "editSocialLinks",
  },
  projects: {
    add: "projectAdd",
    edit: "projectEdit",
  },
  skills: {
    edit: "editSkills",
  },
  tickets: {
    add: "openTicket",
  },
  freeMaterial: {
    reviewsForm: "freeMaterialsReviewsForm",
  },
  round: {
    mindMap: "roundMindMap",
  },
  utils: {
    showText: "showText",
  },
} as const

export type Modals = NestedValues<typeof MODALS>

// Define types that match your MODALS structure
export type ModalsDataMap = {
  advertisements: {
    index: {
      advertisementModal?: undefined
    }
  }
  confirm: {
    index: {
      confirmModal?: undefined
    }
  }
  notifications: {
    view: {
      notificationView?: undefined
    }
  }
  enrolledRounds: {
    notes: {
      roundNotes?: TaskSubmission
    }
  }
  lecture: {
    video: {
      videoModal?: { value?: string }
    }
    uplaodTask: {
      taskupload?: Lecture
    }
    preivewDrive: {
      driveVideoPreview?: {
        videos: Array<{ url: string }>
        index: number
      }
    }
    previewAdditionalMaterial: {
      additionalMaterialPreview?: {
        materials: Array<{ url: string }>
        index: number
      }
    }
    tasks: {
      lectureTasks?: { lecture: Lecture; course: Course }
    }
    projects: {
      lectureProjects?: { lecture: Lecture; course: Course }
    }
    mentorSessions: {
      lectureMentorSessions?: { lecture: Lecture; course: Course }
    }
    additionalMaterials: {
      lectureAdditionalMaterial?: { lecture: Lecture; course: Course }
    }
    content: {
      lectureContent?: Lecture
    }
    attend: {
      attendLecture?: {
        lecture: Lecture
        round: Round
      }
    }
  }
  feedbacks: {
    index: {
      feedback?: undefined
    }
    all: {
      allFeedbacks?: undefined
    }
    profile: {
      editSocialLinks?: ProfileLinks
    }
  }
  projects: {
    add: {
      projectAdd?: boolean
    }
    edit: {
      projectEdit?: StudentProject
    }
  }
  skills: {
    edit: {
      editSkills?: Array<SkillDTO>
    }
  }
  tickets: {
    add: {
      openTicket?: boolean
    }
  }
  freeMaterial: {
    reviewsForm: {
      freeMaterialsReviewsForm?: FreeMaterialReview | boolean
    }
  }
  round: {
    mindMap: {
      roundMindMap?: {
        course_content?: CourseContent
      }
    }
  }
  utils: {
    showText: {
      showText?: string
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (
  k: infer I
) => void
  ? I
  : never

type FlattenCategory<T> =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T extends Record<string, any>
    ? UnionToIntersection<
        {
          [K in keyof T]: T[K]
        }[keyof T]
      >
    : never

export type FlatModalDataMap = UnionToIntersection<
  {
    [Category in keyof ModalsDataMap]: FlattenCategory<ModalsDataMap[Category]>
  }[keyof ModalsDataMap]
>

export type SetModalData = <TModalName extends keyof FlatModalDataMap>(
  modalName: TModalName,
  data: FlatModalDataMap[TModalName]
) => void
